import { call, takeLatest, put, fork, all } from 'redux-saga/effects';
import { sortBy } from 'lodash';
import * as apiXero from '../../services/xero/api_xero';
import * as apiPaymentType from '../../services/payment_type/api_payment_type';
import * as apiTillMovemenType from '../../services/till_movement_type/api_till_movement_type';
import * as apiTaxGroup from '../../services/tax/apiTaxGroup';

import * as sagaCommon from '../sagasCommon';
import {
  CONNECT_TO_XERO,
  DISCONNECT_TO_XERO,
  GET_XERO_ACCOUNTS,
  GET_TILL_MOVEMENT_TYPES_XERO,
  GET_TAX_GROUP_XERO,
  GET_XERO_TAX_RATES,
  SAVE_XERO_ACCOUNTS,
  SAVE_XERO_TAX_RATES,
  MANUAL_SYNC,
} from '../../constants/constant_xero';
import {
  setXeroAccounts,
  setPaymentTypesXero,
  setTillMovementTypesXero,
  setTaxGroupsXero,
  setXeroTaxRates,
} from '../../actions/actionCreatorXero';
import { fetchIntegrationById } from '../../actions/actionCreatorIntegration';
import {
  setErrorXeroTaxModal,
  setInfoXeroTaxModal,
  resetXeroTaxModal,
} from '../../components/admin/integrations/integration_tabs/xero/modal/CreateXeroTaxModalActions';
import {
  setErrorXeroAccountModal,
  setInfoXeroAccountModal,
  resetXeroAccountModal,
} from '../../components/admin/integrations/integration_tabs/xero/modal/CreateXeroAccountModalActions';

export default function* (app) {
  try {
    yield all([
      fork(connectXeroSaga, app),
      fork(disconnectXeroSaga, app),
      fork(fetchXeroAccountsSaga, app),
      fork(fetchXeroTaxRatesSaga, app),
      fork(fetchPaymentTypesXeroSaga, app),
      fork(fetchTillMovementTypesXeroSaga, app),
      fork(fetchTaxGroupsXeroSaga, app),
      fork(saveXeroAccountsSaga, app),
      fork(saveXeroTaxRatesSaga, app),
      fork(manualSyncSaga, app),
    ]);
  } catch (e) {
    console.error('@saga_xero: ', e.message || e.stack);
    throw e;
  }
}

function* manualSyncSaga(app) {
  yield takeLatest(MANUAL_SYNC, callManualSyncSaga, app);
}

function* callManualSyncSaga(app, action) {
  try {
    const { id } = action;
    const results = yield call(apiXero.manualSync, app, id);
    let errors = [];
    if (Array.isArray(results.invoice)) {
      const invoiceErrors = results.invoice.map((e) => ({
        message: 'Invoice - ' + e.Message,
      }));
      errors = errors.concat(invoiceErrors);
    }

    if (results.invoice.status && results.invoice.name) {
      errors = errors.concat({ message: 'Invoice - ' + results.invoice.message });
    }

    if (Array.isArray(results.credit_note)) {
      const creditNoteErrors = results.credit_note.map((e) => ({
        message: 'Credit note - ' + e.Message,
      }));
      errors = errors.concat(creditNoteErrors);
    }

    if (results.credit_note.status && results.credit_note.name) {
      errors = errors.concat({ message: 'Credit note - ' + results.credit_note.message });
    }

    if (Array.isArray(results.manual_journal)) {
      const manualJournalErrors = results.manual_journal.map((e) => ({
        message: 'Manual journal - ' + e.Message,
      }));
      errors = errors.concat(manualJournalErrors);
    }

    if (results.manual_journal.status && results.manual_journal.name) {
      errors = errors.concat({ message: 'Manual journal - ' + results.manual_journal.message });
    }

    if (errors.length) {
      yield sagaCommon.showErrorSaveRecord(errors);
    }
  } catch (error) {
    yield sagaCommon.showErrorDataById(error);
  }
}

function* connectXeroSaga(app) {
  yield takeLatest(CONNECT_TO_XERO, callConnectXeroSaga, app);
}

function* callConnectXeroSaga(app, action) {
  try {
    const { id } = action;
    yield call(apiXero.authorise, app, id);
  } catch (error) {
    yield sagaCommon.showErrorDataById(error);
  }
}

function* disconnectXeroSaga(app) {
  yield takeLatest(DISCONNECT_TO_XERO, callDisconnectXeroSaga, app);
}

function* callDisconnectXeroSaga(app, action) {
  try {
    yield sagaCommon.showProgressSaveRecord();
    const { id } = action;
    const doc = yield call(apiXero.disconnect, app, id);
    yield put(fetchIntegrationById(id));
    yield sagaCommon.doneSaveRecord(doc, 'Successfully disconnected from Xero');
  } catch (error) {
    yield sagaCommon.showErrorSaveRecord(error);
  }
}

function* fetchXeroAccountsSaga(app) {
  yield takeLatest(GET_XERO_ACCOUNTS, callFetchXeroAccounts, app);
}

function* callFetchXeroAccounts(app, action) {
  try {
    const { id } = action;
    const params = { integrationId: id };
    const result = yield call(apiXero.fetchAccountDropdown, app, params);
    yield put(setXeroAccounts(result));
  } catch (error) {
    yield sagaCommon.showErrorDataById(error);
  }
}

function* saveXeroAccountsSaga(app) {
  yield takeLatest(SAVE_XERO_ACCOUNTS, callSaveXeroAccounts, app);
}

function* callSaveXeroAccounts(app, action) {
  try {
    const { integrationId, data } = action;
    const params = { integrationId };
    yield call(apiXero.saveAccount, app, data, params);
    yield put({ type: GET_XERO_ACCOUNTS, id: integrationId });
    yield put(
      setInfoXeroAccountModal({
        message: 'Successfully saved new account in Xero',
      }),
    );
    yield put(resetXeroAccountModal());
  } catch (error) {
    yield put(setErrorXeroAccountModal(error));
  }
}

function* fetchXeroTaxRatesSaga(app) {
  yield takeLatest(GET_XERO_TAX_RATES, callFetchXeroTaxRates, app);
}

function* callFetchXeroTaxRates(app, action) {
  try {
    const { id } = action;
    const params = { integrationId: id };
    const result = yield call(apiXero.fetchTaxRateDropdown, app, params);
    yield put(setXeroTaxRates(result));
  } catch (error) {
    yield sagaCommon.showErrorDataById(error);
  }
}

function* saveXeroTaxRatesSaga(app) {
  yield takeLatest(SAVE_XERO_TAX_RATES, callSaveXeroTaxRates, app);
}

function* callSaveXeroTaxRates(app, action) {
  try {
    const { integrationId, data } = action;
    const params = { integrationId };
    yield call(apiXero.saveTaxRate, app, data, params);
    yield put({ type: GET_XERO_TAX_RATES, id: integrationId });
    yield put(
      setInfoXeroTaxModal({
        message: 'Successfully saved new tax rate in Xero',
      }),
    );
    yield put(resetXeroTaxModal());
  } catch (error) {
    yield put(setErrorXeroTaxModal(error));
  }
}

export function* fetchPaymentTypesXeroSaga(app) {
  yield takeLatest(GET_XERO_ACCOUNTS, callFetchPaymentTypesXero, app);
}

function* callFetchPaymentTypesXero(app) {
  try {
    const result = yield call(apiPaymentType.fetchAllPaymentTypes, app);
    yield put(setPaymentTypesXero(result));
  } catch (error) {
    yield sagaCommon.showErrorDataById(error);
  }
}

function* fetchTillMovementTypesXeroSaga(app) {
  yield takeLatest(
    GET_TILL_MOVEMENT_TYPES_XERO,
    fetchTillMovementTypesXero,
    app,
  );
}

function* fetchTillMovementTypesXero(app) {
  try {
    const result = yield call(
      apiTillMovemenType.fetchAllTillMovementTypes,
      app,
    );
    const filtered = result.filter(
      (t) =>
        [
          'dt:till_movement_type:tillclose',
          'dt:till_movement_type:tillopen',
          'dt:till_movement_type:manualopen',
          'dt:till_movement_type:variancesod',
        ].indexOf(t.cid) < 0,
    );
    yield put(setTillMovementTypesXero(sortBy(filtered, ['name'])));
  } catch (error) {
    yield sagaCommon.showErrorDataById(error);
  }
}

function* fetchTaxGroupsXeroSaga(app) {
  yield takeLatest(GET_TAX_GROUP_XERO, callFetchTaxGroupsXero, app);
}

function* callFetchTaxGroupsXero(app) {
  try {
    const result = yield call(apiTaxGroup.fetchAllTaxGroups, app);
    yield put(setTaxGroupsXero(result));
  } catch (error) {
    yield sagaCommon.showErrorDataById(error);
  }
}
